/**
 * The /monitor_v2 globe map, framework-free: React (GlobeMonitorMap) owns the
 * data and tells this what to show. Built from the "Shipment Desk" globe
 * prototype, on the same live data and rules as the flat monitor.
 */

import mapboxgl from 'mapbox-gl';
import { getColoredMarker } from '../../Home/map/MapUtils';
import {
  getMapboxLoadTimeoutMs,
  isFatalMapboxLoadError,
} from '../../HomeV2/map/mapboxMapHelpers';
import { MAPBOX_CLUSTER_LABEL_FONT } from '../../Home/map/dashboardPresentation';
import { getHazardIconUrl } from '../../Home/map/hazards/hazardIcons';
import {
  getHazardPresentation,
  inheritHazardPresentation,
  type HazardAlertLike,
} from '../../Home/map/hazards/hazardPresentation';
import {
  filterHazardsByVisibility,
  getHazardRingCenter,
  getRenderableHazardRings,
  isHazardActiveAtTime,
} from '../../Home/map/hazardTypes';
import { laneEventKey } from '../../Home/map/laneEvents';
import {
  applyMonitorDim,
  buildNightPolygon,
  buildRouteEndpointFeatures,
  ENDPOINT_LABEL_ANCHOR,
  ENDPOINT_LABEL_OFFSET,
  markEndpointsUnderRing,
  MONITOR_DIM_OPACITY,
  NIGHT_OPACITY_DARK,
  NIGHT_OPACITY_LIGHT,
  NIGHT_REFRESH_MS,
  ROUTE_DASH,
  ROUTE_GAP,
  ROUTE_DRAW_MS,
  routeDrawProgress,
  trimOffsetFor,
  type RouteEndpoint,
} from '../../Home/map/monitorMapEffects';
import {
  addRouteArrowLayers,
  startRouteArrows,
} from '../../Home/map/routeArrows';
import type { LngLat, Padding } from '../../Home/map/monitorRouteShape';
import type { StreamShipment } from '../../Home/map/needsAttention';
import {
  globeOverview,
  screenHeadingDeg,
  splitRingAtAntimeridian,
  toWrappedMultiLine,
} from './globeGeometry';

/** Camera moves between steps: a gentle turn of the globe, not a spin. */
export const GLOBE_MOVE_MS = 2800;
/** Close-up while the spotlight's route loads (just past the globe). */
const FOCUS_ZOOM = 5.5;
/** Never frame a route closer than this. */
const ROUTE_MAX_ZOOM = 9;
/** A regional overview is framed no closer than this. */
const OVERVIEW_MAX_ZOOM = 6;

const DEFAULT_ICON_COLOR = '#266EF1';
const CLUSTER_ICON = 'globe-cluster';
const BADGE_CRITICAL = 'globe-badge-critical';
const BADGE_AT_RISK = 'globe-badge-at-risk';

type Theme = { dark: boolean };

const palette = (dark: boolean) =>
  dark
    ? {
        route: '#4C8DFF',
        label: '#e8eef7',
        halo: '#0d1520',
        laneRing: '#E6A000',
        iconBg: '#1f2733',
      }
    : {
        route: '#1565C0',
        label: '#0d2a4f',
        halo: '#FFFFFF',
        laneRing: '#E6A000',
        iconBg: '#FFFFFF',
      };

/** A stream shipment with the marker fields the stream also sends. */
export type MarkerShipment = StreamShipment & {
  mode?: string;
  icon_color?: string;
};

/** A NewsFlash item: presentation fields plus its polygons. */
type NewsFlashItem = HazardAlertLike & { polygons?: HazardAlertLike[] };

type Collection = GeoJSON.FeatureCollection;
const EMPTY: Collection = { type: 'FeatureCollection', features: [] };

/** "ship|<mode>|<colour>|<alert>|<critical>": one image per marker look. */
const iconKeyFor = (s: MarkerShipment): string =>
  [
    'ship',
    s.mode || 'truck',
    s.icon_color || DEFAULT_ICON_COLOR,
    s.has_alert ? 1 : 0,
    s.alert?.is_critical ? 1 : 0,
  ].join('|');

const shipmentFeatures = (shipments: StreamShipment[]) => ({
  type: 'FeatureCollection' as const,
  features: shipments.map((s: MarkerShipment) => ({
    type: 'Feature' as const,
    properties: {
      shipmentId: String(s.shipment_id),
      iconKey: iconKeyFor(s),
      hasAlert: Boolean(s.has_alert),
      isCritical: Boolean(s.alert?.is_critical),
    },
    geometry: {
      type: 'Point' as const,
      coordinates: [
        Number(s.latest_location?.latest_long),
        Number(s.latest_location?.latest_lat),
      ],
    },
  })),
});

/** Hazard areas (fill/outline) and their icons, in effect now. */
export const hazardFeatures = (
  newsFlashPolygon: unknown,
  nowMs: number
): {
  areas: Collection;
  icons: Collection;
  iconUrls: Record<string, string>;
} => {
  const items = Array.isArray(newsFlashPolygon) ? newsFlashPolygon : [];
  const polygons = filterHazardsByVisibility(
    (items as NewsFlashItem[]).flatMap((item) =>
      Array.isArray(item?.polygons)
        ? item.polygons.map((polygon: HazardAlertLike) =>
            inheritHazardPresentation(item, polygon)
          )
        : []
    ),
    { showWeatherLayer: true, showGeopoliticalLayer: true }
  ).filter((alert) => isHazardActiveAtTime(alert, nowMs));
  const areas: GeoJSON.Feature[] = [];
  const icons: GeoJSON.Feature[] = [];
  const iconUrls: Record<string, string> = {};
  polygons.forEach((alert) => {
    const rings = getRenderableHazardRings(alert?.geometry);
    if (rings.length === 0) return;
    const center = getHazardRingCenter(rings[0]);
    if (!center) return;
    const presentation = getHazardPresentation(alert);
    const key = laneEventKey(alert.hazard_type, center);
    rings.forEach((ring) =>
      splitRingAtAntimeridian(ring.map((p): LngLat => [p.lng, p.lat])).forEach(
        (part) =>
          areas.push({
            type: 'Feature',
            properties: {
              fillColor: presentation.polygon.fillColor,
              strokeColor: presentation.polygon.strokeColor,
              fillOpacity: presentation.polygon.fillOpacity,
            },
            geometry: { type: 'Polygon', coordinates: [part] },
          })
      )
    );
    const icon = `hz-${presentation.markerIconKey}`;
    iconUrls[icon] = getHazardIconUrl(presentation.markerIconKey);
    icons.push({
      type: 'Feature',
      properties: { key, icon },
      geometry: { type: 'Point', coordinates: [center.lng, center.lat] },
    });
  });
  return {
    areas: { type: 'FeatureCollection', features: areas },
    icons: { type: 'FeatureCollection', features: icons },
    iconUrls,
  };
};

/** A solid round image, drawn once (pixel ratio 2 for sharp TV text). */
const circleImage = (
  size: number,
  fill: string,
  stroke: string,
  strokeWidth: number
) => {
  const ratio = 2;
  const canvas = document.createElement('canvas');
  canvas.width = canvas.height = size * ratio;
  const ctx = canvas.getContext('2d');
  if (!ctx) return null;
  ctx.scale(ratio, ratio);
  ctx.beginPath();
  ctx.arc(size / 2, size / 2, size / 2 - strokeWidth, 0, Math.PI * 2);
  ctx.fillStyle = fill;
  ctx.fill();
  ctx.lineWidth = strokeWidth;
  ctx.strokeStyle = stroke;
  ctx.stroke();
  return ctx.getImageData(0, 0, size * ratio, size * ratio);
};

/** A delayed airport near a shipment on screen, as the globe draws it. */
export interface GlobeAirport {
  icao: string;
  /** Shown under the pin, e.g. "ORD"; falls back to the ICAO code. */
  code: string;
  lat: number;
  lng: number;
  /** Departures delayed or cancelled, %. */
  delayPct: number;
}

export interface GlobeRoute {
  traveled: LngLat[];
  remaining: LngLat[];
  /** Picked up; before, the dashed part's arrowheads are slower and fainter. */
  moving?: boolean;
}

export class GlobeMonitorController {
  private map: mapboxgl.Map;
  private ready = false;
  private pending: (() => void)[] = [];
  private nightTimer: number | undefined;
  private loadTimer: number | undefined;
  private drawFrame: number | null = null;
  /** Stops (and clears) the arrowheads on the dashed part; null when none. */
  private stopArrows: (() => void) | null = null;
  /** The route being drawn, and when its draw-in started. */
  private routeKey: unknown = undefined;
  /** Null until the route actually starts drawing (the map may be loading). */
  private routeStartedAt: number | null = null;
  private focusMarker: mapboxgl.Marker | null = null;
  /** Where the focus circle is, and the endpoints whose names it may cover. */
  private ringPosition: { lat: number; lng: number } | null = null;
  private endpoints: RouteEndpoint[] = [];
  private airportMarkers: mapboxgl.Marker[] = [];
  /** The focus ring's arrow: where, and which way, on the ground. */
  private focusHeading: {
    ring: HTMLElement;
    position: { lat: number; lng: number };
    heading: number;
  } | null = null;
  private focusId: string | null = null;
  private laneKeys: string[] = [];
  private destroyed = false;
  private colors: ReturnType<typeof palette>;

  constructor(container: HTMLElement, theme: Theme, onError?: () => void) {
    this.colors = palette(theme.dark);
    this.map = new mapboxgl.Map({
      container,
      style: theme.dark
        ? 'mapbox://styles/mapbox/dark-v11'
        : 'mapbox://styles/mapbox/light-v11',
      projection: 'globe',
      center: [0, 20],
      zoom: 1.4,
      // A wall TV is not handled: no gestures or controls.
      interactive: false,
      // Added below, always expanded: nobody hovers on a wall TV.
      attributionControl: false,
      logoPosition: 'bottom-right',
      fadeDuration: 0,
    });
    // Mapbox requires its text attribution beside the logo.
    this.map.addControl(
      new mapboxgl.AttributionControl({ compact: false }),
      'bottom-right'
    );
    // Only a rejected token before load is fatal (as on the flat map): a
    // late tile or glyph failure must not take a working TV down.
    this.map.on('error', (event) => {
      // Mapbox puts the HTTP status on the error object.
      const error = event?.error as { status?: number } | undefined;
      if (!this.ready && isFatalMapboxLoadError(error)) onError?.();
    });
    // The arrow follows the drawn line as the camera turns the globe.
    this.map.on('move', () => this.orientArrow());
    // Once the camera settles, a place under the circle gets its name lifted.
    this.map.on('moveend', () => this.drawEndpoints());
    this.map.on('styleimagemissing', (e: { id: string }) =>
      this.addShipmentIcon(e.id)
    );
    // A stalled style request sends neither load nor error: without a
    // deadline the TV would stay blank (same deadline as the flat map).
    this.loadTimer = window.setTimeout(() => {
      if (!this.ready && !this.destroyed) onError?.();
    }, getMapboxLoadTimeoutMs('normal'));
    this.map.on('load', () => {
      window.clearTimeout(this.loadTimer);
      this.setup(theme);
      this.ready = true;
      this.pending.splice(0).forEach((fn) => fn());
    });
  }

  /** Adds an image once it has loaded, unless the map is gone by then. */
  private loadImage(id: string, url: string, ratio = 1) {
    if (this.map.hasImage(id)) return;
    const image = new Image();
    image.onload = () => {
      if (!this.destroyed && !this.map.hasImage(id))
        this.map.addImage(id, image, { pixelRatio: ratio });
    };
    image.src = url;
  }

  private whenReady(fn: () => void) {
    if (this.ready) fn();
    else this.pending.push(fn);
  }

  private addShipmentIcon(id: string) {
    if (!id.startsWith('ship|') || this.map.hasImage(id)) return;
    const [, mode, color, alert, critical] = id.split('|');
    // getColoredMarker draws at 2x.
    this.loadImage(
      id,
      getColoredMarker(mode, color, alert === '1', critical === '1'),
      2
    );
  }

  private setup(theme: Theme) {
    const map = this.map;
    const c = this.colors;
    map.setFog(
      theme.dark
        ? {
            color: 'rgb(18,24,44)',
            'high-color': 'rgb(38,64,128)',
            'space-color': '#101317',
            'horizon-blend': 0.05,
            'star-intensity': 0.4,
          }
        : {
            color: 'rgb(236,242,253)',
            'high-color': 'rgb(176,203,246)',
            'space-color': '#F3F3F3',
            'horizon-blend': 0.06,
            'star-intensity': 0,
          }
    );
    const cluster = circleImage(44, '#6B7280', '#FFFFFF', 2.5);
    const critical = circleImage(16, '#D92D20', '#FFFFFF', 2);
    const atRisk = circleImage(16, '#E6A000', '#FFFFFF', 2);
    if (cluster) map.addImage(CLUSTER_ICON, cluster, { pixelRatio: 2 });
    if (critical) map.addImage(BADGE_CRITICAL, critical, { pixelRatio: 2 });
    if (atRisk) map.addImage(BADGE_AT_RISK, atRisk, { pixelRatio: 2 });

    // Night band: under the basemap's labels and everything we draw.
    const firstLabel = map
      .getStyle()
      ?.layers?.find((layer) => layer.type === 'symbol')?.id;
    map.addSource('night', {
      type: 'geojson',
      data: buildNightPolygon(new Date()),
    });
    map.addLayer(
      {
        id: 'night',
        type: 'fill',
        source: 'night',
        paint: {
          'fill-color': theme.dark ? '#000000' : '#0b1e3d',
          'fill-opacity': theme.dark ? NIGHT_OPACITY_DARK : NIGHT_OPACITY_LIGHT,
          'fill-antialias': false,
        },
      },
      firstLabel
    );
    this.nightTimer = window.setInterval(
      () =>
        (map.getSource('night') as mapboxgl.GeoJSONSource)?.setData(
          buildNightPolygon(new Date())
        ),
      NIGHT_REFRESH_MS
    );

    const empty = EMPTY;
    map.addSource('hazard-areas', { type: 'geojson', data: empty });
    map.addLayer({
      id: 'hazard-fill',
      type: 'fill',
      source: 'hazard-areas',
      paint: {
        'fill-color': ['coalesce', ['get', 'fillColor'], '#888'],
        'fill-opacity': ['coalesce', ['get', 'fillOpacity'], 0.35],
      },
    });
    map.addLayer({
      id: 'hazard-line',
      type: 'line',
      source: 'hazard-areas',
      paint: {
        'line-color': ['coalesce', ['get', 'strokeColor'], '#666'],
        'line-width': 1.5,
      },
    });

    map.addSource('route', { type: 'geojson', data: empty, lineMetrics: true });
    // Solid and opaque, with no white casing under them: a see-through line
    // over a soft halo read as blurry on the TV.
    map.addLayer({
      id: 'route-traveled',
      type: 'line',
      source: 'route',
      filter: ['==', ['get', 'segment'], 'traveled'],
      layout: { 'line-cap': 'round', 'line-join': 'round' },
      paint: { 'line-color': c.route, 'line-width': 3.5 },
    });
    map.addLayer({
      id: 'route-remaining',
      type: 'line',
      source: 'route',
      filter: ['==', ['get', 'segment'], 'remaining'],
      layout: { 'line-cap': 'butt', 'line-join': 'round' },
      paint: {
        'line-color': c.route,
        'line-width': 3,
        'line-dasharray': [ROUTE_DASH, ROUTE_GAP],
      },
    });

    // Arrowheads gliding along the dashed part, above the route.
    addRouteArrowLayers(map, 'route-arrows', c.route);

    map.addSource('endpoints', { type: 'geojson', data: empty });
    map.addLayer({
      id: 'endpoint-dots',
      type: 'circle',
      source: 'endpoints',
      filter: ['==', ['get', 'role'], 'dot'],
      paint: {
        'circle-radius': 6,
        'circle-color': [
          'match',
          ['get', 'kind'],
          'destination',
          c.route,
          '#FFFFFF',
        ],
        'circle-stroke-width': 2.5,
        'circle-stroke-color': [
          'match',
          ['get', 'kind'],
          'destination',
          '#FFFFFF',
          c.route,
        ],
      },
    });
    map.addLayer({
      id: 'endpoint-labels',
      type: 'symbol',
      source: 'endpoints',
      filter: ['==', ['get', 'role'], 'label'],
      layout: {
        'text-field': ['get', 'label'],
        'text-font': [...MAPBOX_CLUSTER_LABEL_FONT],
        'text-size': 15,
        'text-anchor':
          ENDPOINT_LABEL_ANCHOR as mapboxgl.DataDrivenPropertyValueSpecification<'top'>,
        'text-offset':
          ENDPOINT_LABEL_OFFSET as mapboxgl.DataDrivenPropertyValueSpecification<
            [number, number]
          >,
        'text-allow-overlap': true,
      },
      paint: {
        'text-color': c.label,
        'text-halo-color': c.halo,
        'text-halo-width': 2,
      },
    });

    map.addSource('hazard-icons', { type: 'geojson', data: empty });
    map.addLayer({
      id: 'hazard-lane-ring',
      type: 'circle',
      source: 'hazard-icons',
      filter: ['in', ['get', 'key'], ['literal', []]],
      paint: {
        'circle-radius': 17,
        'circle-color': 'rgba(0,0,0,0)',
        'circle-stroke-width': 3,
        'circle-stroke-color': c.laneRing,
      },
    });
    map.addLayer({
      id: 'hazard-icon-bg',
      type: 'circle',
      source: 'hazard-icons',
      paint: {
        'circle-radius': 12,
        'circle-color': c.iconBg,
        'circle-stroke-width': 1,
        'circle-stroke-color': 'rgba(0,0,0,0.15)',
      },
    });
    map.addLayer({
      id: 'hazard-icons',
      type: 'symbol',
      source: 'hazard-icons',
      layout: {
        'icon-image': ['get', 'icon'],
        'icon-size': 0.7,
        'icon-allow-overlap': true,
        'icon-ignore-placement': true,
      },
    });

    map.addSource('shipments', {
      type: 'geojson',
      data: empty,
      cluster: true,
      clusterRadius: 50,
      // On the globe overview everything nearby folds into one bubble.
      clusterMaxZoom: 7,
      clusterProperties: {
        alertCount: ['+', ['case', ['to-boolean', ['get', 'hasAlert']], 1, 0]],
        criticalCount: [
          '+',
          ['case', ['to-boolean', ['get', 'isCritical']], 1, 0],
        ],
      },
    });
    // Same layer ids as the flat map, so applyMonitorDim works on both.
    map.addLayer({
      id: 'shipment-clusters',
      type: 'symbol',
      source: 'shipments',
      filter: ['has', 'point_count'],
      layout: {
        'icon-image': CLUSTER_ICON,
        'icon-allow-overlap': true,
        'text-field': ['get', 'point_count_abbreviated'],
        'text-font': [...MAPBOX_CLUSTER_LABEL_FONT],
        'text-size': 14,
        'text-allow-overlap': true,
      },
      paint: { 'text-color': '#FFFFFF' },
    });
    map.addLayer({
      id: 'shipment-cluster-alert-badges',
      type: 'symbol',
      source: 'shipments',
      filter: [
        'all',
        ['has', 'point_count'],
        [
          'any',
          ['>', ['get', 'criticalCount'], 0],
          ['>', ['get', 'alertCount'], 0],
        ],
      ],
      layout: {
        'icon-image': [
          'case',
          ['>', ['get', 'criticalCount'], 0],
          BADGE_CRITICAL,
          BADGE_AT_RISK,
        ],
        'icon-offset': [16, -16],
        'icon-allow-overlap': true,
        'icon-ignore-placement': true,
      },
    });
    map.addLayer({
      id: 'shipment-points',
      type: 'symbol',
      source: 'shipments',
      filter: ['!', ['has', 'point_count']],
      layout: {
        'icon-image': ['get', 'iconKey'],
        'icon-allow-overlap': true,
        'icon-ignore-placement': true,
      },
    });
  }

  setShipments(shipments: StreamShipment[]) {
    this.whenReady(() =>
      (this.map.getSource('shipments') as mapboxgl.GeoJSONSource)?.setData(
        shipmentFeatures(shipments)
      )
    );
  }

  setHazards(newsFlashPolygon: unknown, nowMs = Date.now()) {
    this.whenReady(() => {
      const { areas, icons, iconUrls } = hazardFeatures(
        newsFlashPolygon,
        nowMs
      );
      Object.entries(iconUrls).forEach(([id, url]) => this.loadImage(id, url));
      (this.map.getSource('hazard-areas') as mapboxgl.GeoJSONSource)?.setData(
        areas
      );
      (this.map.getSource('hazard-icons') as mapboxgl.GeoJSONSource)?.setData(
        icons
      );
    });
  }

  /** Ring the event icons on the focused lane; the rest step back. */
  setLaneKeys(keys: string[]) {
    this.laneKeys = keys;
    this.whenReady(() => this.applyDim());
  }

  /** Everything but the shipment in focus steps back (null restores). */
  setFocusId(focusId: string | null) {
    this.focusId = focusId;
    this.whenReady(() => this.applyDim());
  }

  private applyDim() {
    const map = this.map;
    applyMonitorDim(map, this.focusId);
    const onLane = ['in', ['get', 'key'], ['literal', this.laneKeys]];
    map.setFilter('hazard-lane-ring', onLane);
    const opacity = (
      this.focusId ? ['case', onLane, 1, MONITOR_DIM_OPACITY] : 1
    ) as mapboxgl.DataDrivenPropertyValueSpecification<number>;
    map.setPaintProperty('hazard-icons', 'icon-opacity', opacity);
    map.setPaintProperty('hazard-icon-bg', 'circle-opacity', opacity);
    map.setPaintProperty('hazard-icon-bg', 'circle-stroke-opacity', opacity);
  }

  /**
   * Draws the spotlight's route. A new `key` (a new route) draws itself in;
   * the same key again (detail or panel updates) carries on from where the
   * draw-in had got to instead of restarting or jumping to the end.
   */
  setRoute(route: GlobeRoute | null, key?: unknown) {
    if (!route) this.routeKey = undefined;
    else if (key !== this.routeKey) {
      this.routeKey = key;
      this.routeStartedAt = null;
    }
    this.whenReady(() => {
      // The draw-in starts when it can be seen, not when it was asked for.
      if (this.routeStartedAt === null) this.routeStartedAt = performance.now();
      const startedAt = this.routeStartedAt;
      if (this.drawFrame !== null) cancelAnimationFrame(this.drawFrame);
      this.drawFrame = null;
      const source = this.map.getSource('route') as mapboxgl.GeoJSONSource;
      const part = (segment: string, line: LngLat[]) => {
        const lines = toWrappedMultiLine(line);
        return lines.length
          ? [
              {
                type: 'Feature' as const,
                properties: { segment },
                geometry: {
                  type: 'MultiLineString' as const,
                  coordinates: lines,
                },
              },
            ]
          : [];
      };
      source?.setData({
        type: 'FeatureCollection',
        features: route
          ? [
              ...part('traveled', route.traveled),
              ...part('remaining', route.remaining),
            ]
          : [],
      });
      const layers: [string, 'traveled' | 'remaining'][] = [
        ['route-traveled', 'traveled'],
        ['route-remaining', 'remaining'],
      ];
      const trim = (progress: { traveled: number; remaining: number }) =>
        layers.forEach(([id, segment]) =>
          this.map.setPaintProperty(
            id,
            'line-trim-offset',
            trimOffsetFor(progress[segment])
          )
        );
      // Arrowheads glide along the dashed part towards the destination, from
      // when the line has drawn itself in; slower and fainter before pickup.
      this.stopArrows?.();
      this.stopArrows =
        route && route.remaining.length >= 2
          ? startRouteArrows(
              this.map,
              'route-arrows',
              route.remaining,
              startedAt + ROUTE_DRAW_MS,
              // The globe draws lines within ±180° (see toWrappedMultiLine).
              { wrap: true, planned: !route.moving }
            )
          : null;
      if (!route) return;
      // The route draws itself in: travelled part, then what is left.
      const frame = (now: number) => {
        const progress = routeDrawProgress(now - startedAt);
        trim(progress);
        this.drawFrame =
          progress.remaining < 1 ? requestAnimationFrame(frame) : null;
      };
      frame(performance.now());
    });
  }

  setEndpoints(endpoints: RouteEndpoint[]) {
    this.endpoints = endpoints;
    this.whenReady(() => this.drawEndpoints());
  }

  private drawEndpoints() {
    if (!this.ready) return;
    (this.map.getSource('endpoints') as mapboxgl.GeoJSONSource)?.setData(
      buildRouteEndpointFeatures(
        markEndpointsUnderRing(this.endpoints, this.ringPosition, (lngLat) =>
          this.map.project(lngLat)
        )
      )
    );
  }

  /**
   * The ring that carries the focused shipment's own marker above the
   * clusters, with the direction-of-travel arrow when there is one.
   */
  setFocusRing(
    shipment: MarkerShipment | null,
    position: { lat: number; lng: number } | null,
    heading: number | null
  ) {
    this.whenReady(() => {
      this.focusMarker?.remove();
      this.focusMarker = null;
      this.focusHeading = null;
      this.ringPosition = shipment && position ? position : null;
      this.drawEndpoints();
      if (!shipment || !position) return;
      const ring = document.createElement('div');
      ring.className = 'monitor-focus-ring';
      const icon = document.createElement('img');
      icon.className = 'monitor-focus-icon';
      icon.alt = '';
      icon.src = getColoredMarker(
        shipment.mode || 'truck',
        shipment.icon_color || DEFAULT_ICON_COLOR,
        Boolean(shipment.has_alert),
        Boolean(shipment.alert?.is_critical)
      );
      ring.appendChild(icon);
      const arrow = document.createElement('div');
      arrow.className = 'monitor-focus-heading';
      ring.appendChild(arrow);
      if (heading !== null) {
        this.focusHeading = { ring, position, heading };
        this.orientArrow();
        ring.classList.add('monitor-focus-ring--heading');
      }
      this.focusMarker = new mapboxgl.Marker({ element: ring })
        .setLngLat([position.lng, position.lat])
        .addTo(this.map);
    });
  }

  /** Points the arrow along the route as it is drawn on screen right now. */
  private orientArrow() {
    if (!this.focusHeading) return;
    const { ring, position, heading } = this.focusHeading;
    const angle = screenHeadingDeg(
      (lngLat) => this.map.project(lngLat),
      position,
      heading
    );
    if (Number.isFinite(angle))
      ring.style.setProperty('--monitor-heading', `${angle}deg`);
  }

  /**
   * Delayed airports, drawn like the flat map's: the pin alone, its ring
   * showing the delay. Their codes and delays are in the trip panel: map
   * labels piled up on the focus circle, which they always sit next to.
   */
  setAirports(airports: GlobeAirport[]) {
    this.whenReady(() => {
      this.airportMarkers.forEach((marker) => marker.remove());
      this.airportMarkers = airports.map((airport) => {
        const el = document.createElement('div');
        el.className = 'monitor-globe-airport';
        const pin = document.createElement('img');
        pin.alt = `${airport.code} ${Math.round(airport.delayPct)}% delayed`;
        pin.src = getColoredMarker(
          'air-delay',
          DEFAULT_ICON_COLOR,
          false,
          false,
          airport.delayPct,
          4
        );
        el.append(pin);
        return new mapboxgl.Marker({ element: el })
          .setLngLat([airport.lng, airport.lat])
          .addTo(this.map);
      });
    });
  }

  private viewport() {
    const el = this.map.getContainer();
    return { width: el.clientWidth, height: el.clientHeight };
  }

  /** Face the globe towards the fleet (or frame a regional one). */
  showOverview(points: { lat: number; lng: number }[], padding: Padding) {
    this.whenReady(() => {
      const view = globeOverview(points, this.viewport(), padding);
      if (view.kind === 'fit') {
        this.fit(
          view.points.map((p): LngLat => [p.lng, p.lat]),
          padding,
          OVERVIEW_MAX_ZOOM
        );
        return;
      }
      this.map.easeTo({
        center: [view.center.lng, view.center.lat],
        zoom: view.zoom,
        padding,
        bearing: 0,
        pitch: 0,
        duration: GLOBE_MOVE_MS,
        essential: true,
      });
    });
  }

  /** Turn to the shipment and come in while its route loads. */
  focusOn(position: { lat: number; lng: number }, padding: Padding) {
    this.whenReady(() =>
      this.map.flyTo({
        center: [position.lng, position.lat],
        zoom: FOCUS_ZOOM,
        padding,
        bearing: 0,
        pitch: 0,
        duration: GLOBE_MOVE_MS,
        essential: true,
      })
    );
  }

  /** Frame the whole route (a long one stays on the globe, which suits it). */
  frameRoute(coords: LngLat[], padding: Padding) {
    this.whenReady(() => this.fit(coords, padding, ROUTE_MAX_ZOOM));
  }

  private fit(coords: LngLat[], padding: Padding, maxZoom: number) {
    if (coords.length === 0) return;
    const lngs = coords.map(([lng]) => lng);
    const lats = coords.map(([, lat]) => lat);
    // West/east may run past ±180 so the box crosses the date line.
    this.map.fitBounds(
      [
        [Math.min(...lngs), Math.min(...lats)],
        [Math.max(...lngs), Math.max(...lats)],
      ],
      {
        padding,
        maxZoom,
        bearing: 0,
        pitch: 0,
        duration: GLOBE_MOVE_MS,
        essential: true,
      }
    );
  }

  destroy() {
    this.destroyed = true;
    window.clearTimeout(this.loadTimer);
    window.clearInterval(this.nightTimer);
    if (this.drawFrame !== null) cancelAnimationFrame(this.drawFrame);
    this.stopArrows?.();
    this.focusMarker?.remove();
    this.airportMarkers.forEach((marker) => marker.remove());
    this.map.remove();
  }
}
